import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Gift, Calculator, CheckCircle, Loader, AlertCircle, ChevronDown, ChevronUp, Users, TrendingUp } from 'lucide-react';

const fmt = (v) => `PKR ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const BonusPool = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [calculating, setCalculating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState(null);

  const [pools, setPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [currentPool, setCurrentPool] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [loadingDist, setLoadingDist] = useState(false);
  const [expandedPool, setExpandedPool] = useState(null);

  const fetchPools = useCallback(async () => {
    try {
      setLoadingPools(true);
      const res = await api.get('/bonus-pools/');
      setPools(res.data.results || res.data || []);
    } catch (err) {
      console.error('Failed to fetch bonus pools:', err);
    } finally {
      setLoadingPools(false);
    }
  }, []);

  useEffect(() => { fetchPools(); }, [fetchPools]);

  const handleCalculate = async () => {
    setCalculating(true);
    setError(null);
    try {
      const res = await api.post('/bonus-pools/calculate/', { year, month });
      setCurrentPool(res.data);
      await fetchPools();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate bonus pool.');
    } finally {
      setCalculating(false);
    }
  };

  const handleFinalize = async () => {
    if (!currentPool) return;
    if (!window.confirm(`Finalize bonus pool for ${MONTH_NAMES[currentPool.month - 1]} ${currentPool.year}? This will create bonus records for all eligible employees and cannot be undone.`)) return;
    setFinalizing(true);
    setError(null);
    try {
      const res = await api.post(`/bonus-pools/${currentPool.id}/finalize/`);
      setCurrentPool(res.data);
      await fetchPools();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to finalize bonus pool.');
    } finally {
      setFinalizing(false);
    }
  };

  const toggleDistributions = async (pool) => {
    if (expandedPool === pool.id) {
      setExpandedPool(null);
      return;
    }
    setExpandedPool(pool.id);
    setLoadingDist(true);
    try {
      const res = await api.get(`/bonus-pools/${pool.id}/distributions/`);
      setDistributions(res.data);
    } catch (err) {
      console.error('Failed to fetch distributions:', err);
    } finally {
      setLoadingDist(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Gift className="text-green-600" size={24} />
          Bonus Pool
        </h1>
        <p className="text-sm text-gray-500 mt-1">Calculate and distribute monthly bonuses from new installs and shifted customers</p>
      </div>

      {/* Calculator Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
          <Calculator size={16} className="text-blue-600" />
          Calculate Pool
        </h2>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {MONTH_NAMES.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-24"
              min="2020"
              max="2100"
            />
          </div>
          <button
            onClick={handleCalculate}
            disabled={calculating}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
          >
            {calculating ? <Loader size={16} className="animate-spin" /> : <Calculator size={16} />}
            {calculating ? 'Calculating…' : 'Calculate'}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Result Card */}
        {currentPool && (
          <div className="mt-6 border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                  {MONTH_NAMES[currentPool.month - 1]} {currentPool.year}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5">{fmt(currentPool.total_bonus_amount)}</p>
                <p className="text-xs text-gray-500 mt-0.5">Total Bonus Pool</p>
              </div>
              {currentPool.is_finalized ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                  <CheckCircle size={14} /> Finalized
                </span>
              ) : (
                <button
                  onClick={handleFinalize}
                  disabled={finalizing || currentPool.total_bonus_amount <= 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {finalizing ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  {finalizing ? 'Finalizing…' : 'Finalize & Distribute'}
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100">
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500">New Installs</p>
                <p className="text-xl font-bold text-gray-900">{currentPool.new_installs_count}</p>
                <p className="text-xs text-green-600 mt-0.5">× PKR 500 each</p>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500">Shifted Customers</p>
                <p className="text-xl font-bold text-gray-900">{currentPool.shifted_count}</p>
                <p className="text-xs text-orange-600 mt-0.5">× PKR 200 each</p>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500">Eligible Employees</p>
                <p className="text-xl font-bold text-gray-900">{currentPool.eligible_employees_count}</p>
                <p className="text-xs text-gray-400 mt-0.5">bonus eligible & active</p>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500">Per Employee</p>
                <p className="text-xl font-bold text-blue-700">{fmt(currentPool.per_employee_bonus)}</p>
                <p className="text-xs text-gray-400 mt-0.5">equal share</p>
              </div>
            </div>

            {currentPool.is_finalized && currentPool.finalized_by_username && (
              <div className="px-5 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
                Finalized by <span className="font-medium text-gray-600">{currentPool.finalized_by_username}</span>
                {currentPool.finalized_at && ` on ${new Date(currentPool.finalized_at).toLocaleString()}`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pool History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <TrendingUp size={16} className="text-gray-400" />
          <h2 className="font-semibold text-gray-800">Pool History</h2>
        </div>

        {loadingPools ? (
          <div className="p-8 text-center"><Loader className="animate-spin text-blue-600 mx-auto" size={24} /></div>
        ) : pools.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Gift size={40} className="mx-auto mb-2 text-gray-200" />
            <p className="text-sm">No bonus pools yet. Calculate one above.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pools.map((pool) => (
              <div key={pool.id}>
                <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-semibold text-gray-900">{MONTH_NAMES[pool.month - 1]} {pool.year}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {pool.new_installs_count} new · {pool.shifted_count} shifted · {pool.eligible_employees_count} employees
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{fmt(pool.total_bonus_amount)}</p>
                      <p className="text-xs text-blue-600">{fmt(pool.per_employee_bonus)} / person</p>
                    </div>
                    {pool.is_finalized ? (
                      <span className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                        <CheckCircle size={12} /> Done
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">Draft</span>
                    )}
                    <button
                      onClick={() => toggleDistributions(pool)}
                      className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <Users size={13} />
                      Breakdown
                      {expandedPool === pool.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>

                {/* Distribution breakdown */}
                {expandedPool === pool.id && (
                  <div className="px-6 pb-4 bg-gray-50 border-t border-gray-100">
                    {loadingDist ? (
                      <div className="py-4 text-center"><Loader size={18} className="animate-spin text-blue-500 mx-auto" /></div>
                    ) : distributions.length === 0 ? (
                      <p className="text-sm text-gray-400 py-4 text-center">No distributions yet. Finalize the pool to create them.</p>
                    ) : (
                      <table className="w-full text-sm mt-3">
                        <thead>
                          <tr className="text-left">
                            <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Employee</th>
                            <th className="pb-2 text-xs font-semibold text-gray-500 uppercase text-right">Bonus</th>
                            <th className="pb-2 text-xs font-semibold text-gray-500 uppercase text-center">Linked to Salary</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {distributions.map((d) => (
                            <tr key={d.id}>
                              <td className="py-2 font-medium text-gray-900">{d.employee_name}</td>
                              <td className="py-2 text-right font-semibold text-green-700">{fmt(d.amount)}</td>
                              <td className="py-2 text-center">
                                {d.salary_payment
                                  ? <span className="text-green-600 text-xs font-medium">Yes #{d.salary_payment}</span>
                                  : <span className="text-gray-400 text-xs">Pending</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BonusPool;
